/**
 * @module util/portPicker/placesService
 * Wrapper over the **Places API (New) REST** endpoints — deliberately NO Google Maps JS SDK.
 *
 * The legacy Dropdown loaded the Maps JS SDK (react-google-maps `withScriptjs`); the smart
 * picker replaced it. Loading the JS SDK from the picker collided with other in-page Maps
 * loads (react-google-maps injects its own `/maps/api/js` at a different version) — two Maps
 * loads scramble the SDK internals and break `AutocompleteSuggestion`. Calling the REST API
 * directly avoids that entirely: no script, no version, no load-order races.
 *
 * Degrades gracefully: with no API key it's unavailable and the picker falls back to a
 * PF-port/airport-only list (UX §6). The Maps key is a public client key (it already ships in
 * the bundle) — protect it with an HTTP-referrer (Websites) restriction on the GCP key, not
 * secrecy. Places API (New) supports CORS for referrer-restricted keys.
 */
import axios from 'axios';
import { G_MAP_API_KEY } from '../../envVariables';

const BASE = 'https://places.googleapis.com/v1';
const keyHeader = () => ({ 'Content-Type': 'application/json', 'X-Goog-Api-Key': G_MAP_API_KEY });

/** True when a Maps API key is configured (REST needs only the key — no SDK to load). */
export const isAvailable = () => !!G_MAP_API_KEY;

/** Fresh autocomplete session token (one per "type → pick" cycle) for session billing.
 *  null when crypto is unavailable — autocomplete still works, just billed per-request. */
export const newSessionToken = () => {
  const c = window.crypto;
  return c && typeof c.randomUUID === 'function' ? c.randomUUID() : null;
};

/** Autocomplete predictions for `input`. Resolves [] on empty input, missing key, or any error. */
export const getPredictions = async (input, sessionToken) => {
  if (!input?.trim() || !G_MAP_API_KEY) return [];
  try {
    const body = { input };
    if (sessionToken) body.sessionToken = sessionToken;
    const res = await axios.post(`${BASE}/places:autocomplete`, body, { headers: keyHeader(), timeout: 10000 });
    const suggestions = res?.data?.suggestions || [];
    return suggestions
      .map((s) => s.placePrediction)
      .filter(Boolean)
      .map((p) => ({
        placeId: p.placeId,
        label: p.text?.text || '',
        primary: p.structuredFormat?.mainText?.text || p.text?.text || '',
        secondary: p.structuredFormat?.secondaryText?.text || '',
      }));
  } catch (e) {
    console.warn('[places] autocomplete failed:', e?.message);
    return []; // referrer / quota / network error → fall back to PF-port-only list
  }
};

/** Resolve a placeId → { lat, lng, country, countryId, label }. null on missing key or any error. */
export const getPlaceDetails = async (placeId, sessionToken) => {
  if (!placeId || !G_MAP_API_KEY) return null;
  try {
    const res = await axios.get(`${BASE}/places/${encodeURIComponent(placeId)}`, {
      headers: { ...keyHeader(), 'X-Goog-FieldMask': 'location,formattedAddress,displayName,addressComponents' },
      params: sessionToken ? { sessionToken } : undefined,
      timeout: 10000,
    });
    const place = res?.data;
    if (!place?.location) return null;
    const countryComp = (place.addressComponents || []).find((c) => (c.types || []).includes('country'));
    return {
      lat: place.location.latitude,
      lng: place.location.longitude,
      country: countryComp ? countryComp.longText : null,
      countryId: countryComp ? countryComp.shortText : null, // ISO2
      label: place.formattedAddress || place.displayName?.text || '',
    };
  } catch (e) {
    console.warn('[places] details failed:', e?.message);
    return null;
  }
};
